import EditAliasPolicyModal from "../../Components/EditAliasPolicyModal";
import UnlinkPolicyModal from "../../Components/UnlinkPolicyModal";
import { usePolicyContext } from "../../Contexts/AddPolicyContext";
import ErrorComponent from "../../Components/ErrorComponent";
import { baseUrl } from "../../utilities/configurations";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAuth } from "../../Contexts/AuthContext";
import IconButton from "@mui/material/IconButton";
import apiRequest from "../../utilities/request";
import { CircularProgress } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Button from "../../Components/Button";
import { useEffect, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Menu from "@mui/material/Menu";
import Tab from "@mui/material/Tab";

const Policies = () => {
  const { setSelectedPolicy, setShowModal, showModal, navigate } =
    usePolicyContext();
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [loadState, setLoadState] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [tabIndex, setTabIndex] = useState("0");
  const { userData, setUserData } = useAuth();
  const open = Boolean(anchorEl);

  const handleClick = (event, policy) => {
    setAnchorEl(event.currentTarget);
    setCurrentPolicy(policy);
  };

  const handleMenuItemClick = (action) => {
    if (currentPolicy) {
      if (action === "edit") {
        setSelectedPolicy(currentPolicy);
        setShowModal("edit");
      } else if (action === "unlink") {
        setSelectedPolicy(currentPolicy);
        setShowModal("unlink");
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentPolicy(null);
  };

  const handleChangeTab = async (e, val) => {
    setTabIndex(val);
    // Fetch policies based on the selected tab
    await getPolicy();
  };

  const getPolicy = async (state) => {
    try {
      setLoadState(state || "load");
      const res = await apiRequest(baseUrl + "/policy/policies", "GET");
      if (!res.error) {
        setUserData(res.data || []);
        setLoadState("load success");
      } else if (res.status >= 500) {
        setLoadState("ISE");
      } else {
        setLoadState("load failed");
      }
    } catch (error) {
      setLoadState("timeout");
    }
  };

  // Fetch policies when tab changes
  useEffect(() => {
    getPolicy();
  }, [tabIndex]);

  const handleEditAliasSuccess = () => {
    getPolicy(); // Refetch policies after successful alias update
  };

  const handleUnlinkPolicySuccess = () => {
    getPolicy(); // Refetch policies after successful unlinking policy
  };

  function capitalizeWords(name) {
    if (typeof name !== "string") {
      return ""; // Return an empty string if input is not a string
    }
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  function formatSuffix(suffix) {
    if (suffix === "JR." || suffix === "SR.") {
      return suffix.charAt(0).toUpperCase() + suffix.slice(1).toLowerCase();
    }
    return suffix;
  }

  const activePolicies =
    userData?.filter((user) => user.memberStatus === "ACTIVE") || [];
  const inactivePolicies =
    userData?.filter((user) => user.memberStatus === "INACTIVE") || [];
  const hasInactivePolicies = inactivePolicies.length > 0;

  const filteredPolicies = tabIndex === "0" ? activePolicies : inactivePolicies;

  let content = null;
  if (loadState === "load") {
    content = (
      <div className="w-full flex justify-center items-center p-3">
        <CircularProgress />
      </div>
    );
  } else if (["ISE", "timeout", "retry"].includes(loadState)) {
    content = (
      <ErrorComponent
        onRetry={() => getPolicy("retry")}
        isLoading={loadState === "retry"}
      />
    );
  } else {
    content = (
      <div className="p-3 flex flex-col">
        {filteredPolicies.length > 0 ? (
          filteredPolicies.map((user) => {
            let renderPolicyName =
              user.alias.charAt(0).toUpperCase() +
              user.alias.slice(1).toLowerCase();

            if (user.alias !== user.plan?.planType) {
              renderPolicyName = user.alias;
            }

            return (
              <div
                className={`flex flex-row border rounded-lg p-3 my-2 ${
                  tabIndex === "1" ? "bg-[#F6F7F7]" : ""
                }`}
                key={user.cardNo}
              >
                <div
                  className={`text-sm flex flex-col flex-grow ${
                    tabIndex === "1" ? "cursor-default" : "cursor-pointer"
                  }`}
                  onClick={() => {
                    if (tabIndex === "0") {
                      setSelectedPolicy(user);
                      navigate("/add-policy/details");
                    }
                  }}
                >
                  <div className="font-bold">{renderPolicyName}</div>
                  <div className="pt-1">
                    {`${capitalizeWords(
                      user.firstName || ""
                    )} ${capitalizeWords(
                      user.middleName || ""
                    )} ${capitalizeWords(user.lastName || "")} ${
                      user.suffixName ? formatSuffix(user.suffixName) : ""
                    }
    `}
                  </div>
                </div>

                {tabIndex === "0" && ( // Only show menu for active policies
                  <div>
                    <IconButton
                      aria-label="more"
                      id="long-button"
                      aria-controls={open ? "long-menu" : undefined}
                      aria-expanded={open ? "true" : undefined}
                      aria-haspopup="true"
                      onClick={(event) => handleClick(event, user)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      id="long-menu"
                      MenuListProps={{ "aria-labelledby": "long-button" }}
                      anchorEl={anchorEl}
                      open={open && currentPolicy === user}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem onClick={() => handleMenuItemClick("edit")}>
                        <div className="flex flex-row items-center justify-center">
                          <img
                            src={require("../../assets/edit.png")}
                            alt="edit"
                            width="25px"
                            height="25px"
                          />
                          <div className="text-[#1F2328] ml-2">
                            Edit policy alias
                          </div>
                        </div>
                      </MenuItem>

                      <MenuItem
                        onClick={() => handleMenuItemClick("unlink")}
                        disabled={user.isPrimary}
                        style={{
                          color: "#B70000",
                        }}
                      >
                        <div className="flex flex-row items-center justify-center">
                          <img
                            src={require("../../assets/unlink.png")}
                            alt="unlink"
                            width="25px"
                            height="25px"
                          />
                          <div className="ml-2">Unlink policy</div>
                        </div>
                      </MenuItem>
                    </Menu>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p className="text-center text-sm text-[#61646B]">
            No policies available
          </p>
        )}

        {tabIndex === "0" && ( // Only show this section in the active tab
          <div className="flex flex-col sm:items-end items-center w-full">
            <div className="w-full my-3">
              <p className="text-center text-sm text-[#61646B]">
                Protect your family with Maxicare! Tap 'Add Maxicare card to
                include dependents for enhanced coverage'
              </p>
            </div>
            <Button
              className="h-14 w-full text-white"
              onClick={() => {
                navigate("/add-policy/choose-membership");
              }}
            >
              Add Maxicare card
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center min-h-screen max-w-screen sm:bg-[#E7EEFA]">
      {showModal === "unlink" ? (
        <UnlinkPolicyModal onSuccess={handleUnlinkPolicySuccess} />
      ) : showModal === "edit" ? (
        <EditAliasPolicyModal onSuccess={handleEditAliasSuccess} />
      ) : null}

      <div className="bg-white w-screen mb-[56px] sm:mb-24 lg:mb-12 sm:mt-12 sm:w-148 h-auto rounded-xl">
        <div className="w-full grid grid-cols-1 divide-y">
          <div className="text-center text-md font-bold mb-3 mt-3">
            Policies
          </div>
          <Tabs
            sx={{ width: "100%" }}
            value={tabIndex}
            onChange={handleChangeTab}
            aria-label="policies-tab"
          >
            <Tab
              className="grow md:grow-0"
              sx={{ textTransform: "none", fontSize: "14px" }}
              value="0"
              label="Active"
            />
            <Tab
              className="grow md:grow-0"
              sx={{ textTransform: "none", fontSize: "14px" }}
              value="1"
              label="Inactive"
              disabled={!hasInactivePolicies} // Disable tab if no inactive policies
            />
          </Tabs>
          <div className="p-2">{content}</div>
        </div>
      </div>
    </div>
  );
};

export default Policies;
